import api from './api';
import axios from 'axios';

// Create a dedicated axios instance for supervisor API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const supervisorClient = axios.create({
  baseURL: API_BASE_URL
});

// Helper function to validate token format
const validateToken = (token) => {
  if (!token) {
    console.error('[SupervisorAPI] No authentication token provided');
    throw new Error('Authentication required. Please log in.');
  }
  
  // Basic JWT format validation (should have 3 parts separated by dots)
  const parts = token.split('.');
  if (parts.length !== 3) {
    console.error('[SupervisorAPI] Invalid token format');
    throw new Error('Invalid authentication token. Please log in again.');
  }
  
  return true;
};

// Note: Token is now passed explicitly to each function
// No interceptor needed as we handle auth headers in each request

// Start a new supervisor consultation
export const startConsultation = async (clientId, conversationId, initialContext, token) => {
  try {
    validateToken(token);
    const response = await supervisorClient.post('/supervisor/consultations', {
      clientId,
      conversationId,
      initialContext
    }, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[SupervisorAPI] startConsultation error:', error);
    throw error;
  }
};

// Get a specific consultation
export const getConsultation = async (consultationId, token) => {
  try {
    validateToken(token);
    const response = await supervisorClient.get(`/supervisor/consultations/${consultationId}`, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[SupervisorAPI] getConsultation error:', error);
    throw error;
  }
};

// Send a message in a consultation
export const sendMessage = async (consultationId, message, token) => {
  try {
    validateToken(token);
    const response = await supervisorClient.post(`/supervisor/consultations/${consultationId}/messages`, {
      message
    }, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[SupervisorAPI] sendMessage error:', error);
    throw error;
  }
};

// Save consultation notes
export const saveConsultationNotes = async (consultationId, summary, keyInsights = [], recommendations = [], wasEdited = false, token) => {
  try {
    validateToken(token);
    
    const response = await supervisorClient.post(`/supervisor/consultations/${consultationId}/save-notes`, {
      summary,
      keyInsights,
      recommendations,
      wasEdited
    }, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[SupervisorAPI] saveConsultationNotes error:', error);
    throw error;
  }
};

// Create a care note
export const createCareNote = async (consultationId, careNote, token) => {
  try {
    validateToken(token);
    const response = await supervisorClient.post(`/supervisor/consultations/${consultationId}/care-notes`, careNote, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[SupervisorAPI] createCareNote error:', error);
    throw error;
  }
};

// Toggle the active flag on an existing care note subdoc.
// Used by ConsultationSummaryReview to activate AI-suggested notes in
// place instead of POSTing fresh manual copies (which produced ghost
// duplicates — original AI suggestion left as active:false, plus a new
// active manual copy with identical content).
export const setCareNoteActive = async (consultationId, careNoteId, active, token) => {
  try {
    validateToken(token);
    const response = await supervisorClient.patch(
      `/supervisor/care-notes/${consultationId}/${careNoteId}`,
      { active },
      { headers: { 'x-auth-token': token } }
    );
    return response.data;
  } catch (error) {
    console.error('[SupervisorAPI] setCareNoteActive error:', error);
    throw error;
  }
};

// Complete a consultation
export const completeConsultation = async (consultationId, token) => {
  try {
    validateToken(token);
    const response = await supervisorClient.post(`/supervisor/consultations/${consultationId}/complete`, {}, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[SupervisorAPI] completeConsultation error:', error);
    throw error;
  }
};

// Get consultation history
export const getConsultationHistory = async (params = {}, token) => {
  try {
    validateToken(token);
    const response = await supervisorClient.get('/supervisor/consultations', { 
      params,
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[SupervisorAPI] getConsultationHistory error:', error);
    throw error;
  }
};

// Get active care notes for a client
export const getClientCareNotes = async (clientId, token) => {
  try {
    validateToken(token);
    const response = await supervisorClient.get(`/supervisor/care-notes/client/${clientId}`, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[SupervisorAPI] getClientCareNotes error:', error);
    throw error;
  }
};

// Toggle care note active status
export const toggleCareNoteStatus = async (consultationId, careNoteId, active, token) => {
  try {
    validateToken(token);
    const response = await supervisorClient.patch(`/supervisor/care-notes/${consultationId}/${careNoteId}`, {
      active
    }, {
      headers: { 'x-auth-token': token }
    });
    return response.data;
  } catch (error) {
    console.error('[SupervisorAPI] toggleCareNoteStatus error:', error);
    throw error;
  }
};

// Get consultation statistics
export const getConsultationStatistics = async (token, params = {}) => {
  try {
    validateToken(token);
    const response = await supervisorClient.get('/supervisor/statistics', {
      headers: { 'x-auth-token': token },
      params
    });
    return response.data;
  } catch (error) {
    console.error('[SupervisorAPI] getConsultationStatistics error:', error);
    throw error;
  }
};